import { fakeZoom } from "@/model/Settings";

import { MAX_CELL_HEIGHT, MIN_CELL_HEIGHT, teamHeight } from "./computeLayout";
import type { TeamCounts } from "./computeLayout";

export interface LayoutInputs {
  boardWidth: number;
  boardHeight: number;
  teams: readonly TeamCounts[];
}

export interface NoWrapLayout {
  // cellH / MAX_CELL_HEIGHT — the board applies it as `--art-scale`.
  scale: number;
  // One entry per column (variable, K_MIN..K_MAX). Trailing empties render
  // blank. Each column lists the indices of the teams stacked in it, in order —
  // a team is never split across columns.
  columns: number[][];
}

// Fewest columns we ever render (small boards still fill a grid).
const K_MIN = 4;
// Most columns we allow. Above this cells get too narrow to read.
const K_MAX = 8;

/**
 * Lay out the ART Objectives board. For each candidate column count K in
 * [K_MIN..K_MAX], find the largest scale that packs every team (in order,
 * whole, greedy next-fit) into K columns. Pick the K whose scale is largest;
 * ties break to smaller K — a column is only "spent" when it buys back cell
 * size. Always returns a renderable layout.
 */
export function computeNoWrapLayout(input: LayoutInputs): NoWrapLayout {
  if (
    input.teams.length === 0 ||
    !Number.isFinite(input.boardWidth) ||
    input.boardWidth <= 0 ||
    !Number.isFinite(input.boardHeight) ||
    input.boardHeight <= 0
  ) {
    // No teams or a degenerate/unmeasured board → blank columns (recomputes
    // once a real size arrives).
    return { scale: 1, columns: pad([], K_MIN) };
  }
  const columnHeight = input.boardHeight / fakeZoom;

  let best: { K: number; cellH: number; columns: number[][] } | null = null;
  for (let K = K_MIN; K <= K_MAX; K++) {
    for (let cellH = MAX_CELL_HEIGHT; cellH >= MIN_CELL_HEIGHT; cellH--) {
      const cols = packColumns(input.teams, cellH, columnHeight, K, false);
      if (cols) {
        if (!best || cellH > best.cellH) best = { K, cellH, columns: cols };
        // Larger cellH values were already rejected for this K.
        break;
      }
    }
  }

  if (best) {
    return { scale: best.cellH / MAX_CELL_HEIGHT, columns: pad(best.columns, best.K) };
  }

  // No K in [K_MIN..K_MAX] fits at MIN scale — overflow fallback at K_MAX
  // (last column scrolls, excess columns merged into it).
  const columns = packColumns(input.teams, MIN_CELL_HEIGHT, columnHeight, K_MAX, true)!;
  return { scale: MIN_CELL_HEIGHT / MAX_CELL_HEIGHT, columns: pad(columns, K_MAX) };
}

/**
 * Greedy next-fit (like text line-wrapping): fill the current column with
 * consecutive teams until one doesn't fit, then close it and start the next; a
 * closed column is never revisited. Because teams keep their fixed order this
 * minimizes the column count, and each team is kept whole (never split).
 *
 * Strict mode (allowOverflow=false, the scale search): returns null if a team
 * can't fit its own column or the teams need more than columnCount columns — so
 * the outer loop shrinks the scale until everything genuinely fits.
 * Overflow mode (allowOverflow=true, the floor fallback): a too-tall team keeps
 * its own scrolling column, and excess columns are merged into the last.
 */
function packColumns(
  teams: readonly TeamCounts[],
  cellH: number,
  columnHeight: number,
  columnCount: number,
  allowOverflow: boolean,
): number[][] | null {
  const columns = new Array<number[]>();
  let current = new Array<number>();
  let used = 0;
  for (let i = 0; i < teams.length; i++) {
    const h = teamHeight(teams[i], cellH);
    if (current.length > 0 && used + h <= columnHeight) {
      current.push(i);
      used += h;
    } else {
      if (current.length > 0) columns.push(current);
      // A team taller than a whole column doesn't fit at this scale — reject so
      // the outer loop shrinks it (the floor fallback lets it scroll instead).
      if (h > columnHeight && !allowOverflow) return null;
      current = [i];
      used = h;
    }
  }
  if (current.length > 0) columns.push(current);

  if (columns.length <= columnCount) return columns;
  if (!allowOverflow) return null;
  // Too many columns even at the floor — merge the overflow into the last
  // column (it scrolls) rather than dropping teams.
  while (columns.length > columnCount) {
    const tail = columns.pop()!;
    columns[columns.length - 1].push(...tail);
  }
  return columns;
}

/** Grow the column list to exactly columnCount so trailing tracks render blank. */
function pad(columns: number[][], columnCount: number): number[][] {
  const padded = [...columns];
  while (padded.length < columnCount) padded.push([]);
  return padded;
}
